import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { useAuth } from '../auth/useAuth'
import {
  ChatbotService,
  type Attachment,
  type BalanceRead,
  type ChatPlanRead,
  type ConnectRequestRead,
  type MessagePage,
  type MessageRead,
  type PendingTurnRead,
  type PermissionAction,
  type PermissionDecision,
  type PermissionRead,
  type PlanDecision,
  type PlanStepInput,
  type SummarizationScope,
  type ThreadPage,
  type ThreadRead,
} from '../../client'
import { apiErrorMessage } from '../../lib/api'
import { messageToolResults } from '../../lib/messageContent'
import { streamDecision, streamPlanDecision, streamTurn, type StreamEvent } from '../../lib/chatStream'
import posthog from '../../lib/posthog'
import { posthogLogs } from '../../lib/posthogLogs'
import { appendStored, drain, hasPending, reduce, type Block } from '../../lib/inFlight'
import { ChatContext, InFlightContext, NEW_CHAT_SETTINGS, type InFlightStore } from './context'

const TICK_MS = 16

const RESUME_POLL_MS = 3000

const ROUND_FETCH = 10
const ROUND_FETCH_MAX = 100
const POLL_FAILURES_ALLOWED = 3
const TURN_FAILED = 'Something went wrong — please try again.'

export function ChatProvider({ children }: { children: ReactNode }) {
  const { brand } = useAuth()
  const brandId = brand?.id

  const [threads, setThreads] = useState<ThreadRead[]>([])
  const [threadsCursor, setThreadsCursor] = useState<string | null>(null)
  const [tabIds, setTabIds] = useState<string[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)
  const [messages, setMessages] = useState<MessageRead[]>([])
  const [messagesCursor, setMessagesCursor] = useState<string | null>(null)
  const [permissions, setPermissions] = useState<PermissionRead[]>([])
  const [balance, setBalance] = useState<BalanceRead | null>(null)
  const [pendingPermission, setPendingPermission] = useState<PendingTurnRead | null>(null)
  const [pendingSummarization, setPendingSummarization] = useState<SummarizationScope[] | null>(null)
  const [connectRequest, setConnectRequest] = useState<ConnectRequestRead | null>(null)
  const [plans, setPlans] = useState<ChatPlanRead[]>([])
  const [planMode, setPlanMode] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<'sending' | 'deciding' | 'summarizing' | null>(null)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [loadingMoreThreads, setLoadingMoreThreads] = useState(false)

  const activeIdRef = useRef<string | null>(null)
  activeIdRef.current = activeId
  const turnRef = useRef<AbortController | null>(null)
  const messagesRef = useRef<MessageRead[]>([])
  messagesRef.current = messages

  const pagesRef = useRef(new Map<string, { messages: MessageRead[]; cursor: string | null }>())
  const loadIdRef = useRef(0)
  const resumeIdRef = useRef(0)
  const resumingRef = useRef(new Set<string>())
  const pendingIdRef = useRef<string | null>(null)
  const lastSentRef = useRef<{ userMessage: string; attachments: Attachment[] } | null>(null)
  const blocksRef = useRef<Block[] | null>(null)
  const listenersRef = useRef(new Set<() => void>())
  const inFlightStore = useMemo<InFlightStore>(
    () => ({
      subscribe: (listener) => {
        listenersRef.current.add(listener)
        return () => listenersRef.current.delete(listener)
      },
      get: () => blocksRef.current,
    }),
    [],
  )

  const setInFlight = useCallback((next: Block[] | null | ((blocks: Block[] | null) => Block[] | null)) => {
    blocksRef.current = typeof next === 'function' ? next(blocksRef.current) : next
    for (const listener of listenersRef.current) listener()
  }, [])

  useEffect(() => () => turnRef.current?.abort(), [])

  useEffect(() => {
    if (activeId) pagesRef.current.set(activeId, { messages, cursor: messagesCursor })
  }, [activeId, messages, messagesCursor])

  const applyThreads = useCallback((page: ThreadPage) => {
    setThreads(page.threads)
    setThreadsCursor(page.next_cursor ?? null)
    return page.threads
  }, [])

  useEffect(() => {
    if (!brandId) return

    ChatbotService.listThreads(brandId).then(applyThreads).catch(() => undefined)
    ChatbotService.getPermissions(brandId)
      .then((page) => setPermissions(page.permissions))
      .catch(() => undefined)
    ChatbotService.getCreditBalance(brandId)
      .then(setBalance)
      .catch(() => undefined)
  }, [brandId, applyThreads])

  const streaming = busy !== null
  useEffect(() => {
    if (!streaming) return

    const handle = setInterval(() => {
      const blocks = blocksRef.current
      if (blocks && hasPending(blocks)) setInFlight(drain(blocks))
    }, TICK_MS)
    return () => clearInterval(handle)
  }, [streaming, setInFlight])

  const showCards = useCallback((page: MessagePage) => {
    setPendingPermission(page.pending ?? null)
    setConnectRequest(page.connect ?? null)
    setPendingSummarization(page.summarization ?? null)
    setPlans(page.plans ?? [])
  }, [])

  const loadMessages = useCallback(async (brand: number, threadId: string, cursor?: string) => {
    const request = ++loadIdRef.current
    setLoadingMessages(true)
    try {
      const page = await ChatbotService.getThreadMessages(threadId, brand, undefined, cursor)
      // the user may have moved on while this was in flight
      if (loadIdRef.current !== request || activeIdRef.current !== threadId) return null

      const ordered = [...page.messages].sort((a, b) => a.sequence - b.sequence)
      setMessages((current) => (cursor ? [...ordered, ...current] : ordered))
      setMessagesCursor(page.next_cursor ?? null)
      if (!cursor) showCards(page)
      return page
    } finally {
      // only the newest request owns the spinner, so a superseded one cannot hide it
      if (loadIdRef.current === request) setLoadingMessages(false)
    }
  }, [showCards])

  /** Abandon whatever is on screen, including any turn still streaming into it. */
  const clearConversation = useCallback(
    (threadId: string | null, seed?: { messages: MessageRead[]; cursor: string | null }) => {
      turnRef.current?.abort()
      turnRef.current = null
      loadIdRef.current += 1
      resumeIdRef.current += 1
      setActiveId(threadId)
      setMessages(seed?.messages ?? [])
      setMessagesCursor(seed?.cursor ?? null)
      setPendingPermission(null)
      setPendingSummarization(null)
      setConnectRequest(null)
      setPlans([])
      setError(null)
      setInFlight(null)
      setBusy(null)
      setLoadingMessages(false)
    },
    [setInFlight],
  )

  const openThread = useCallback(
    async (threadId: string) => {
      if (!brandId) return

      setTabIds((current) => (current.includes(threadId) ? current : [...current, threadId]))
      if (threadId === activeIdRef.current && messagesRef.current.length > 0) {
        void resumeRef.current(threadId, null, resumeIdRef.current)
        return
      }

      clearConversation(threadId, pagesRef.current.get(threadId))
      const generation = resumeIdRef.current
      const page = await loadMessages(brandId, threadId).catch((err: unknown) => {
        setError(apiErrorMessage(err))
        return null
      })
      if (page?.messages.length === 0) {
        setTabIds((current) => current.filter((id) => id !== threadId))
        clearConversation(null)
        toast.info('Conversation not found')
        return
      }
      void resumeRef.current(threadId, page ?? null, generation)
    },
    [brandId, loadMessages, clearConversation],
  )

  async function resume(threadId: string, page: MessagePage | null, generation: number) {
    if (!brandId || resumingRef.current.has(threadId)) return
    resumingRef.current.add(threadId)

    const mine = () => activeIdRef.current === threadId && resumeIdRef.current === generation

    const settle = () => {
      if (!mine()) return
      setInFlight(null)
      setBusy(null)
    }

    const commit = async () => {
      while (mine() && hasPending(blocksRef.current ?? [])) {
        await new Promise((done) => setTimeout(done, TICK_MS))
      }
      await loadMessages(brandId, threadId).catch(() => undefined)
      settle()
      void ChatbotService.listThreads(brandId).then(applyThreads).catch(() => undefined)
    }

    try {
      let polled = false
      let failures = 0
      let revealedThrough =
        page?.messages.at(-1)?.sequence ?? messagesRef.current.at(-1)?.sequence ?? 0
      let latest =
        page ?? (await ChatbotService.getThreadMessages(threadId, brandId, 1).catch(() => null))

      while (latest && mine()) {
        if (latest.state !== 'in_progress') break

        if (!polled) {
          polled = true
          setInFlight([])
          setBusy('sending')
        }
        await new Promise((done) => setTimeout(done, RESUME_POLL_MS))
        if (!mine()) return

        const probe = await ChatbotService.getThreadMessages(threadId, brandId, 1).catch(() => null)
        if (!mine()) return
        if (!probe) {
          failures += 1
          if (failures > POLL_FAILURES_ALLOWED) break
          continue
        }
        failures = 0
        setPlans(probe.plans ?? [])

        const newest = probe.messages.at(-1)?.sequence ?? 0
        if (newest > revealedThrough) {
          const span = Math.min(ROUND_FETCH_MAX, Math.max(ROUND_FETCH, newest - revealedThrough))
          const round = await ChatbotService.getThreadMessages(threadId, brandId, span).catch(
            () => null,
          )
          if (!mine()) return

          if (round) {
            const landed = round.messages
            const settledTools = new Set(
              landed.flatMap((message) => messageToolResults(message.content).map(({ id }) => id)),
            )
            for (const message of landed) {
              if (message.sequence <= revealedThrough || message.parent_tool_use_id) continue
              if (message.role === 'assistant') {
                setInFlight((blocks) => appendStored(blocks ?? [], message.content, settledTools))
              }
              const produced = message.attachments ?? []
              if (produced.length > 0) {
                setInFlight((blocks) => [...(blocks ?? []), { kind: 'preview', attachments: produced }])
              }
            }
            revealedThrough = landed.reduce(
              (highest, { sequence }) => Math.max(highest, sequence),
              revealedThrough,
            )
          }
        }

        latest = probe
      }

      if (latest?.state === 'failed') setError(TURN_FAILED)
      if (polled) {
        await commit()
      } else {
        if (latest && mine()) showCards(latest)
        settle()
      }
    } finally {
      resumingRef.current.delete(threadId)
    }
  }

  const resumeRef = useRef(resume)
  resumeRef.current = resume

  /** No thread exists until the first message is sent — this just clears the screen. */
  const newThread = useCallback(() => clearConversation(null), [clearConversation])

  function applyCreditBalance(data: Record<string, unknown>) {
    const { credit_balance: remaining, credits_used: used, credits_total: total } = data
    if (typeof remaining !== 'number' || typeof used !== 'number' || typeof total !== 'number') return
    setBalance({ credits_used: used, credits_total: total, credits_remaining: remaining })
  }

  function handleEvent(threadId: string, event: StreamEvent) {
    if (activeIdRef.current !== threadId) return

    setInFlight((blocks) => reduce(blocks ?? [], event))

    switch (event.event) {
      case 'turn_started': {
        if (event.data.files_full) toast.info("Files holds 500 at most, so this attachment wasn't saved there")
        // The decision stream re-emits this with only a thread_id.
        const id = event.data.user_message_id
        if (typeof id !== 'string') return
        setMessages((current) =>
          current.map((message) =>
            message.id === pendingIdRef.current
              ? { ...message, id, sequence: Number(event.data.sequence ?? message.sequence) }
              : message,
          ),
        )
        pendingIdRef.current = null
        return
      }
      case 'credit_balance':
        applyCreditBalance(event.data)
        return
      case 'plan_updated': {
        const plan = event.data as ChatPlanRead
        setPlans((current) => {
          const others = current.filter(({ id }) => id !== plan.id)
          const retired = new Set(['proposed', 'paused', 'running'])
          return [
            ...others.map((other) =>
              retired.has(other.status) && plan.status === 'proposed' ? { ...other, status: 'revised' as const } : other,
            ),
            plan,
          ]
        })
        return
      }
      case 'complete': {
        const title = event.data.thread_title
        if (typeof title === 'string' && title && activeIdRef.current) {
          const threadId = activeIdRef.current
          setThreads((current) =>
            current.map((thread) => (thread.id === threadId ? { ...thread, title } : thread)),
          )
        }
        return
      }
      case 'error':
        setError(String(event.data.error || 'The assistant ran into an error.'))
        applyCreditBalance(event.data)
        return
    }
  }

  async function runStream(
    threadId: string,
    operation: 'chat_message' | 'permission_decision' | 'plan_decision',
    start: (signal: AbortSignal) => Promise<void>,
  ) {
    const turn = new AbortController()
    turnRef.current = turn
    setError(null)
    setInFlight([])

    try {
      await start(turn.signal)
      if (turn.signal.aborted) return
      posthogLogs.info('chat_stream_completed', { operation })
      // show the finished reply in full while the stored messages are refetched
      setInFlight((blocks) => (blocks ? drain(blocks, true) : blocks))
      if (brandId && activeIdRef.current === threadId) await loadMessages(brandId, threadId)
    } catch (err) {
      if (!turn.signal.aborted) {
        posthogLogs.error('chat_stream_failed', { operation })
        setError(apiErrorMessage(err))
      }
    } finally {
      if (turnRef.current === turn) {
        turnRef.current = null
        setInFlight(null)
        setBusy(null)
      }
    }
  }

  async function send(userMessage: string, attachments: Attachment[], settings = NEW_CHAT_SETTINGS) {
    if (!brandId || busy) return
    setConnectRequest(null)

    // Open the conversation before the thread exists, so the first send feels instant —
    // creating it is a round trip the user should never wait on.
    const pendingId = `pending:${Date.now()}`
    const startedOn = activeIdRef.current
    pendingIdRef.current = pendingId
    lastSentRef.current = { userMessage, attachments }

    setBusy('sending')
    setError(null)
    setInFlight([])
    setMessages((current) => [
      ...current,
      {
        id: pendingId,
        brand_id: brandId,
        thread_id: startedOn ?? '',
        sequence: (current.at(-1)?.sequence ?? 0) + 1,
        role: 'user',
        content: userMessage,
        attachments,
        created_at: new Date().toISOString(),
      } as MessageRead,
    ])

    function abandon(message: string | null) {
      setMessages((current) => current.filter(({ id }) => id !== pendingId))
      if (message) setError(message)
      setInFlight(null)
      setBusy(null)
    }

    let threadId = startedOn
    if (!threadId) {
      try {
        const thread = await ChatbotService.createThread(brandId, settings.useBrandBook, settings.useFiles)
        // the user can navigate while the thread is being created
        if (activeIdRef.current !== startedOn) {
          abandon(null)
          return
        }
        setThreads((current) => [thread, ...current])
        setTabIds((current) => [...current, thread.id])
        setActiveId(thread.id)
        activeIdRef.current = thread.id
        threadId = thread.id
      } catch (err) {
        abandon(apiErrorMessage(err))
        return
      }
    }

    const id = threadId
    const mode = planMode ? 'plan' : 'chat'
    setPlanMode(false)
    posthog.capture('chat_message_sent', {
      has_attachments: attachments.length > 0,
      is_new_conversation: startedOn === null,
      mode,
    })
    await runStream(id, 'chat_message', (signal) =>
      streamTurn({
        brandId,
        threadId: id,
        userMessage,
        attachments,
        mode,
        signal,
        onEvent: (event) => handleEvent(id, event),
      }),
    )
  }

  async function decidePlan(planId: string, decision: PlanDecision, feedback = '') {
    if (!brandId || !activeId || busy) return

    const threadId = activeId
    lastSentRef.current = null
    setBusy('deciding')
    posthog.capture('chat_plan_decided', { decision })

    await runStream(threadId, 'plan_decision', (signal) =>
      streamPlanDecision({
        brandId,
        threadId,
        planId,
        decision,
        feedback,
        signal,
        onEvent: (event) => handleEvent(threadId, event),
      }),
    )
  }

  async function editPlan(planId: string, steps: PlanStepInput[]) {
    if (!brandId || !activeId) return
    const updated = await ChatbotService.patchPlan(activeId, planId, brandId, { steps })
    setPlans((current) => current.map((plan) => (plan.id === planId ? updated : plan)))
  }

  async function decide(decision: PermissionDecision) {
    if (!brandId || !activeId || !pendingPermission) return

    const { state_token: stateToken } = pendingPermission
    const threadId = activeId
    setPendingPermission(null)
    setBusy('deciding')
    posthog.capture('chat_permission_decided', { decision })

    await runStream(threadId, 'permission_decision', (signal) =>
      streamDecision({
        brandId,
        threadId,
        stateToken,
        decision,
        signal,
        onEvent: (event) => handleEvent(threadId, event),
      }),
    )

    if (decision === 'always') {
      await ChatbotService.getPermissions(brandId)
        .then((page) => setPermissions(page.permissions))
        .catch(() => undefined)
    }
  }

  async function summarize(scope: SummarizationScope) {
    if (!brandId || !activeId) return

    setBusy('summarizing')
    try {
      await ChatbotService.postSummarize(activeId, brandId, { scope })
      setPendingSummarization(null)
      const [page, credits] = await Promise.all([
        ChatbotService.listThreads(brandId),
        ChatbotService.getCreditBalance(brandId),
      ])
      applyThreads(page)
      setBalance(credits)
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setBusy(null)
    }

    // The backend asks to summarize *before* storing the message, so resend it.
    const pending = lastSentRef.current
    lastSentRef.current = null
    if (pending) await send(pending.userMessage, pending.attachments)
  }

  async function rename(threadId: string, title: string) {
    if (!brandId) return
    const updated = await ChatbotService.patchThread(threadId, brandId, { title })
    setThreads((current) => current.map((thread) => (thread.id === threadId ? updated : thread)))
  }

  async function archive(threadId: string) {
    if (!brandId) return

    await ChatbotService.patchThread(threadId, brandId, { archived: true })
    setThreads((current) => current.filter((thread) => thread.id !== threadId))
    closeTab(threadId)
  }

  function closeTab(threadId: string) {
    const remaining = tabIds.filter((id) => id !== threadId)
    setTabIds(remaining)
    if (activeId !== threadId) return

    const next = remaining.at(-1)
    if (next) {
      void openThread(next)
    } else {
      setActiveId(null)
      setMessages([])
    }
  }

  async function loadMoreThreads() {
    if (!brandId || !threadsCursor || loadingMoreThreads) return

    setLoadingMoreThreads(true)
    try {
      const page = await ChatbotService.listThreads(brandId, false, undefined, threadsCursor)
      setThreads((current) => {
        const seen = new Set(current.map(({ id }) => id))
        return [...current, ...page.threads.filter(({ id }) => !seen.has(id))]
      })
      setThreadsCursor(page.next_cursor ?? null)
    } finally {
      setLoadingMoreThreads(false)
    }
  }

  async function loadOlderMessages() {
    if (!brandId || !activeId || !messagesCursor || loadingMessages) return
    await loadMessages(brandId, activeId, messagesCursor)
  }

  async function setFeedback(messageId: string, feedback: string | null) {
    if (!brandId) return
    const updated = await ChatbotService.patchMessage(messageId, brandId, { feedback })
    setMessages((current) => current.map((message) => (message.id === messageId ? updated : message)))
    if (feedback) posthog.capture('chat_feedback_submitted', { feedback })
  }

  async function revoke(toolName: string, action: PermissionAction) {
    if (!brandId) return
    await ChatbotService.deletePermission(brandId, toolName, action)
    setPermissions((current) =>
      current.filter((item) => !(item.tool_name === toolName && item.action === action)),
    )
  }

  return (
    <ChatContext
      value={{
        brandId,
        threads,
        tabs: tabIds
          .map((id) => threads.find((thread) => thread.id === id))
          .filter((thread): thread is ThreadRead => Boolean(thread)),
        activeId,
        messages,
        permissions,
        balance,
        pendingPermission,
        pendingSummarization,
        connectRequest,
        plans,
        planMode,
        error,
        busy,
        loadingMessages,
        loadingMoreThreads,
        hasMoreThreads: threadsCursor !== null,
        hasMoreMessages: messagesCursor !== null,
        locked: pendingPermission !== null || pendingSummarization !== null,
        openThread,
        newThread,
        closeTab,
        send,
        decide,
        setPlanMode,
        decidePlan,
        editPlan,
        summarize,
        rename,
        archive,
        loadMoreThreads,
        loadOlderMessages,
        setFeedback,
        revoke,
        refreshThreads: () =>
          brandId ? ChatbotService.listThreads(brandId).then(applyThreads) : Promise.resolve([]),
      }}
    >
      <InFlightContext value={inFlightStore}>{children}</InFlightContext>
    </ChatContext>
  )
}
